import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component, inject } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  Validators,
  ReactiveFormsModule,
  FormArray,
} from '@angular/forms';
import { Router } from '@angular/router'; // Fixed the Router import
import { NgToastService } from 'ng-angular-popup';
import { ProjectsService } from '@/app/services/projects/projects.service';

@Component({
  selector: 'app-scope-work',
  standalone: true,
  imports: [ReactiveFormsModule, CommonModule],
  templateUrl: './scope-work.component.html',
  styleUrls: ['./scope-work.component.css'],
})
export class ScopeWorkComponent {
  form: FormGroup;

  showTitleError = false;
  showFileError = false;

  fb = inject(FormBuilder);

  constructor(
    private http: HttpClient,
    private projectService: ProjectsService,
    private toast: NgToastService,
    private router: Router // Added the correct import for Router
  ) {
    // Initialize form in constructor without conditional logic
    this.form = this.fb.group({
      scope: ['', Validators.required],

      qualityData: this.fb.array([
        this.fb.group({
          qualityTitle: [''],
          qualityDescription: [''],
          qualityFile: [[]], // Initialize as an empty array to hold multiple files
          qualityStatus: [''], // Validators will be added dynamically
        }),
      ]),

      specificationData: this.fb.array([
        this.fb.group({
          specificationTitle: [''],
          specificationDescription: [''],
          specificationFile: [[]], // Initialize as an empty array to hold multiple files
          specificationStatus: [''], // Validators will be added dynamically
        }),
      ]),

      requirementData: this.fb.array([
        this.fb.group({
          requirementTitle: [''],
          requirementDescription: [''],
          requirementFile: [[]], // Initialize as an empty array to hold multiple files
          requirementStatus: [''], // Validators will be added dynamically
        }),
      ]),

      testingData: this.fb.array([
        this.fb.group({
          testingTitle: [''],
          testingDescription: [''],
          testingFile: [[]], // Initialize as an empty array to hold multiple files
          testingStatus: [''], // Validators will be added dynamically
        }),
      ]),

      standardData: this.fb.array([
        this.fb.group({
          standardTitle: [''],
          standardDescription: [''],
          standardFile: [[]], // Initialize as an empty array to hold multiple files
          standardStatus: [''], // Validators will be added dynamically
        }),
      ]),

      permitData: this.fb.array([
        this.fb.group({
          permitTitle: [''],
          permitDescription: [''],
          permitFile: [[]], // Initialize as an empty array to hold multiple files
          permitStatus: [''], // Validators will be added dynamically
        }),
      ]),

      costBreakDownData: this.fb.array([
        this.fb.group({
          costBreakDownTitle: [''],
          costBreakDownDescription: [''],
          costBreakDownFile: [[]], // Initialize as an empty array to hold multiple files
          costBreakDownStatus: [''], // Validators will be added dynamically
        }),
      ]),
    });
  }

  get qualityGroups(): FormArray {
    return this.form.get('qualityData') as FormArray;
  }

  addQualityForm() {
    this.qualityGroups.push(
      this.fb.group({
        qualityTitle: [''],
        qualityDescription: [''],
        qualityFile: [[]], // Initialize as an empty array to hold multiple files
        qualityStatus: [''],
      })
    );
  }

  get specificationGroups(): FormArray {
    return this.form.get('specificationData') as FormArray;
  }

  addSpecificationForm() {
    this.specificationGroups.push(
      this.fb.group({
        specificationTitle: [''],
        specificationDescription: [''],
        specificationFile: [[]], // Initialize as an empty array to hold multiple files
        specificationStatus: [''],
      })
    );
  }

  get standardGroups(): FormArray {
    return this.form.get('standardData') as FormArray;
  }

  addStandardForm() {
    this.standardGroups.push(
      this.fb.group({
        standardTitle: [''],
        standardDescription: [''],
        standardFile: [[]], // Initialize as an empty array to hold multiple files
        standardStatus: [''],
      })
    );
  }

  get testingGroups(): FormArray {
    return this.form.get('testingData') as FormArray;
  }

  addTestingForm() {
    this.testingGroups.push(
      this.fb.group({
        testingTitle: [''],
        testingDescription: [''],
        testingFile: [[]], // Initialize as an empty array to hold multiple files
        testingStatus: [''],
      })
    );
  }

  get requirementGroups(): FormArray {
    return this.form.get('requirementData') as FormArray;
  }

  addRequirementForm() {
    this.requirementGroups.push(
      this.fb.group({
        requirementTitle: [''],
        requirementDescription: [''],
        requirementFile: [[]], // Initialize as an empty array to hold multiple files
        requirementStatus: [''],
      })
    );
  }

  get permitGroups(): FormArray {
    return this.form.get('permitData') as FormArray;
  }

  addPermitForm() {
    this.permitGroups.push(
      this.fb.group({
        permitTitle: [''],
        permitDescription: [''],
        permitFile: [[]], // Initialize as an empty array to hold multiple files
        permitStatus: [''],
      })
    );
  }

  get costBreakDownGroups(): FormArray {
    return this.form.get('costBreakDownData') as FormArray;
  }

  addCostBreakDownForm() {
    this.costBreakDownGroups.push(
      this.fb.group({
        costBreakDownTitle: [''],
        costBreakDownDescription: [''],
        costBreakDownFile: [[]], // Initialize as an empty array to hold multiple files
        costBreakDownStatus: [''],
      })
    );
  }

  onFileUpload(event: any, fileType: string, index: number): void {
    const input = event.target as HTMLInputElement;

    if (input.files && input.files.length > 0) {
      const files: File[] = Array.from(input.files);
      const updatedFiles = files.map((file) => {
        return {
          name: file.name,
          type: file.type,
          url: URL.createObjectURL(file), // Creating URL for the file preview
        };
      });

      if (fileType === 'qualityFile') {
        const qualityDataArray = this.form.get('qualityData') as FormArray;
        const qualityGroup = qualityDataArray.at(index) as FormGroup;

        const existingFiles = qualityGroup.get('qualityFile')?.value || [];

        const mergedFiles = [...existingFiles, ...updatedFiles].filter(
          (file, idx, self) =>
            idx ===
            self.findIndex((f) => f.name === file.name && f.type === file.type)
        );

        qualityGroup.patchValue({ qualityFile: mergedFiles });

        // Set validators for qualityTitle and qualityStatus
        if (updatedFiles.length > 0) {
          qualityGroup.get('qualityTitle')?.setValidators(Validators.required);
          qualityGroup.get('qualityStatus')?.setValidators(Validators.required);
        } else {
          qualityGroup.get('qualityTitle')?.clearValidators();
          qualityGroup.get('qualityStatus')?.clearValidators();
        }

        qualityGroup.get('qualityTitle')?.updateValueAndValidity();
        qualityGroup.get('qualityStatus')?.updateValueAndValidity();
      }

      if (fileType === 'specificationFile') {
        const specificationDataArray = this.form.get(
          'specificationData'
        ) as FormArray;
        const specificationGroup = specificationDataArray.at(
          index
        ) as FormGroup;

        const existingFiles =
          specificationGroup.get('specificationFile')?.value || [];
        const mergedFiles = [...existingFiles, ...updatedFiles].filter(
          (file, idx, self) =>
            idx ===
            self.findIndex((f) => f.name === file.name && f.type === file.type)
        );

        specificationGroup.patchValue({ specificationFile: mergedFiles });

        // Set validators for specificationTitle and specificationStatus
        if (updatedFiles.length > 0) {
          specificationGroup
            .get('specificationTitle')
            ?.setValidators(Validators.required);
          specificationGroup
            .get('specificationStatus')
            ?.setValidators(Validators.required);
        } else {
          specificationGroup.get('specificationTitle')?.clearValidators();
          specificationGroup.get('specificationStatus')?.clearValidators();
        }

        specificationGroup.get('specificationTitle')?.updateValueAndValidity();
        specificationGroup.get('specificationStatus')?.updateValueAndValidity();
      }

      if (fileType === 'standardFile') {
        const standardDataArray = this.form.get('standardData') as FormArray;
        const standardGroup = standardDataArray.at(index) as FormGroup;

        const existingFiles = standardGroup.get('standardFile')?.value || [];
        const mergedFiles = [...existingFiles, ...updatedFiles].filter(
          (file, idx, self) =>
            idx ===
            self.findIndex((f) => f.name === file.name && f.type === file.type)
        );

        standardGroup.patchValue({ standardFile: mergedFiles });

        // Set validators for standardTitle and standardStatus
        if (updatedFiles.length > 0) {
          standardGroup
            .get('standardTitle')
            ?.setValidators(Validators.required);
          standardGroup
            .get('standardStatus')
            ?.setValidators(Validators.required);
        } else {
          standardGroup.get('standardTitle')?.clearValidators();
          standardGroup.get('standardStatus')?.clearValidators();
        }

        standardGroup.get('standardTitle')?.updateValueAndValidity();
        standardGroup.get('standardStatus')?.updateValueAndValidity();
      }

      if (fileType === 'requirementFile') {
        const requirementDataArray = this.form.get(
          'requirementData'
        ) as FormArray;
        const requirementGroup = requirementDataArray.at(index) as FormGroup;

        const existingFiles =
          requirementGroup.get('requirementFile')?.value || [];
        const mergedFiles = [...existingFiles, ...updatedFiles].filter(
          (file, idx, self) =>
            idx ===
            self.findIndex((f) => f.name === file.name && f.type === file.type)
        );

        requirementGroup.patchValue({ requirementFile: mergedFiles });

        // Set validators for requirementTitle and requirementStatus
        if (updatedFiles.length > 0) {
          requirementGroup
            .get('requirementTitle')
            ?.setValidators(Validators.required);
          requirementGroup
            .get('requirementStatus')
            ?.setValidators(Validators.required);
        } else {
          requirementGroup.get('requirementTitle')?.clearValidators();
          requirementGroup.get('requirementStatus')?.clearValidators();
        }

        requirementGroup.get('requirementTitle')?.updateValueAndValidity();
        requirementGroup.get('requirementStatus')?.updateValueAndValidity();
      }

      if (fileType === 'testingFile') {
        const testingDataArray = this.form.get('testingData') as FormArray;
        const testingGroup = testingDataArray.at(index) as FormGroup;

        const existingFiles = testingGroup.get('testingFile')?.value || [];
        const mergedFiles = [...existingFiles, ...updatedFiles].filter(
          (file, idx, self) =>
            idx ===
            self.findIndex((f) => f.name === file.name && f.type === file.type)
        );

        testingGroup.patchValue({ testingFile: mergedFiles });

        // Set validators for testingTitle and testingStatus
        if (updatedFiles.length > 0) {
          testingGroup.get('testingTitle')?.setValidators(Validators.required);
          testingGroup.get('testingStatus')?.setValidators(Validators.required);
        } else {
          testingGroup.get('testingTitle')?.clearValidators();
          testingGroup.get('testingStatus')?.clearValidators();
        }

        testingGroup.get('testingTitle')?.updateValueAndValidity();
        testingGroup.get('testingStatus')?.updateValueAndValidity();
      }

      if (fileType === 'permitFile') {
        const permitDataArray = this.form.get('permitData') as FormArray;
        const permitGroup = permitDataArray.at(index) as FormGroup;

        const existingFiles = permitGroup.get('permitFile')?.value || [];
        const mergedFiles = [...existingFiles, ...updatedFiles].filter(
          (file, idx, self) =>
            idx ===
            self.findIndex((f) => f.name === file.name && f.type === file.type)
        );

        permitGroup.patchValue({ permitFile: mergedFiles });

        // Set validators for permitTitle and permitStatus
        if (updatedFiles.length > 0) {
          permitGroup.get('permitTitle')?.setValidators(Validators.required);
          permitGroup.get('permitStatus')?.setValidators(Validators.required);
        } else {
          permitGroup.get('permitTitle')?.clearValidators();
          permitGroup.get('permitStatus')?.clearValidators();
        }

        permitGroup.get('permitTitle')?.updateValueAndValidity();
        permitGroup.get('permitStatus')?.updateValueAndValidity();
      }

      if (fileType === 'costBreakDownFile') {
        const costBreakDownDataArray = this.form.get(
          'costBreakDownData'
        ) as FormArray;
        const costBreakDownGroup = costBreakDownDataArray.at(
          index
        ) as FormGroup;

        const existingFiles =
          costBreakDownGroup.get('costBreakDownFile')?.value || [];
        const mergedFiles = [...existingFiles, ...updatedFiles].filter(
          (file, idx, self) =>
            idx ===
            self.findIndex((f) => f.name === file.name && f.type === file.type)
        );

        costBreakDownGroup.patchValue({ costBreakDownFile: mergedFiles });

        // Set validators for costBreakDownTitle and costBreakDownStatus
        if (updatedFiles.length > 0) {
          costBreakDownGroup
            .get('costBreakDownTitle')
            ?.setValidators(Validators.required);
          costBreakDownGroup
            .get('costBreakDownStatus')
            ?.setValidators(Validators.required);
        } else {
          costBreakDownGroup.get('costBreakDownTitle')?.clearValidators();
          costBreakDownGroup.get('costBreakDownStatus')?.clearValidators();
        }

        costBreakDownGroup.get('costBreakDownTitle')?.updateValueAndValidity();
        costBreakDownGroup.get('costBreakDownStatus')?.updateValueAndValidity();
      }
      console.log(fileType, files);
    }
  }

  onSubmit() {
    // console.log("Form values: ", this.form.value);
    if (this.form.valid) {
      const formData = this.form.value;

      const dataToSubmit = new FormData();

      console.log('Scope Data : ', formData.scope);

      dataToSubmit.append('scope', formData.scope);

      // Submit the form using your project service
      this.projectService.addScope(dataToSubmit).subscribe({
        next: (response: any) => {
          console.log('Project scope added:', response);
          this.toast.success('Scope added successfully', 'SUCCESS', 4000);
          this.form.reset();
        },
        error: (error: any) => {
          console.error('Upload failed:', error);
          this.toast.danger('Failed to add scope', 'ERROR', 4000);
        },
      });
    } else {
      if (this.form.get('scope')?.invalid) {
        if (this.form.get('scope')?.errors?.['required']) {
          // alert('Password is required');
        }
      }

      this.form.markAllAsTouched();
      console.log('Form is invalid', this.form);
    }
  }

  onQualitySubmit() {
    const qualityGroup = this.form.get('qualityData') as FormArray;
    // console.log("Form values: ", this.form.value);
    if (qualityGroup.valid) {
      const formData = this.form.value;

      const dataToSubmit = new FormData();

      console.log('Quality Section Data File 1 : ', formData.qualityData);

      dataToSubmit.append('quality_section', formData.qualityData);

      // Submit the form using your project service
      this.projectService.addScope(dataToSubmit).subscribe({
        next: (response: any) => {
          console.log('Project scope added:', response);
          this.toast.success('Scope added successfully', 'SUCCESS', 4000);
          this.form.reset();
        },
        error: (error: any) => {
          console.error('Upload failed:', error);
          this.toast.danger('Failed to add scope', 'ERROR', 4000);
        },
      });
    } else {
      qualityGroup.markAllAsTouched();
      console.log('Form is invalid', this.form);
    }
  }

  onSpecificationSubmit() {
    const specificationGroup = this.form.get('specificationData') as FormArray;
    // console.log("Form values: ", this.form.value);
    if (specificationGroup.valid) {
      const formData = this.form.value;

      const dataToSubmit = new FormData();

      console.log('specification Section Data File 1 : ', formData.specificationData);

      dataToSubmit.append('specification_section', formData.specificationData);

      // Submit the form using your project service
      this.projectService.addScope(dataToSubmit).subscribe({
        next: (response: any) => {
          console.log('Project scope added:', response);
          this.toast.success('Scope added successfully', 'SUCCESS', 4000);
          this.form.reset();
        },
        error: (error: any) => {
          console.error('Upload failed:', error);
          this.toast.danger('Failed to add scope', 'ERROR', 4000);
        },
      });
    } else {
      specificationGroup.markAllAsTouched();
      console.log('Form is invalid', this.form);
    }
  }

  onRequirementSubmit() {
    const requirementGroup = this.form.get('requirementData') as FormArray;
    // console.log("Form values: ", this.form.value);
    if (requirementGroup.valid) {
      const formData = this.form.value;

      const dataToSubmit = new FormData();

      console.log('requirement Section Data File 1 : ', formData.requirementData);

      dataToSubmit.append('requirement_section', formData.requirementData);

      // Submit the form using your project service
      this.projectService.addScope(dataToSubmit).subscribe({
        next: (response: any) => {
          console.log('Project scope added:', response);
          this.toast.success('Scope added successfully', 'SUCCESS', 4000);
          this.form.reset();
        },
        error: (error: any) => {
          console.error('Upload failed:', error);
          this.toast.danger('Failed to add scope', 'ERROR', 4000);
        },
      });
    } else {
      requirementGroup.markAllAsTouched();
      console.log('Form is invalid', this.form);
    }
  }

  onStandardSubmit() {
    const standardGroup = this.form.get('standardData') as FormArray;
    // console.log("Form values: ", this.form.value);
    if (standardGroup.valid) {
      const formData = this.form.value;

      const dataToSubmit = new FormData();

      console.log('standard Section Data File 1 : ', formData.standardData);

      dataToSubmit.append('standard_section', formData.standardData);

      // Submit the form using your project service
      this.projectService.addScope(dataToSubmit).subscribe({
        next: (response: any) => {
          console.log('Project scope added:', response);
          this.toast.success('Scope added successfully', 'SUCCESS', 4000);
          this.form.reset();
        },
        error: (error: any) => {
          console.error('Upload failed:', error);
          this.toast.danger('Failed to add scope', 'ERROR', 4000);
        },
      });
    } else {
      standardGroup.markAllAsTouched();
      console.log('Form is invalid', this.form);
    }
  }

  onTestingSubmit() {
    const testingGroup = this.form.get('testingData') as FormArray;
    // console.log("Form values: ", this.form.value);
    if (testingGroup.valid) {
      const formData = this.form.value;

      const dataToSubmit = new FormData();

      console.log('testing Section Data File 1 : ', formData.testingData);

      dataToSubmit.append('testing_section', formData.testingData);

      // Submit the form using your project service
      this.projectService.addScope(dataToSubmit).subscribe({
        next: (response: any) => {
          console.log('Project scope added:', response);
          this.toast.success('Scope added successfully', 'SUCCESS', 4000);
          this.form.reset();
        },
        error: (error: any) => {
          console.error('Upload failed:', error);
          this.toast.danger('Failed to add scope', 'ERROR', 4000);
        },
      });
    } else {
      testingGroup.markAllAsTouched();
      console.log('Form is invalid', this.form);
    }
  }

  onPermitSubmit() {
    const permitGroup = this.form.get('permitData') as FormArray;
    // console.log("Form values: ", this.form.value);
    if (permitGroup.valid) {
      const formData = this.form.value;

      const dataToSubmit = new FormData();

      console.log('permit Section Data File 1 : ', formData.permitData);

      dataToSubmit.append('permit_section', formData.permitData);

      // Submit the form using your project service
      this.projectService.addScope(dataToSubmit).subscribe({
        next: (response: any) => {
          console.log('Project scope added:', response);
          this.toast.success('Scope added successfully', 'SUCCESS', 4000);
          this.form.reset();
        },
        error: (error: any) => {
          console.error('Upload failed:', error);
          this.toast.danger('Failed to add scope', 'ERROR', 4000);
        },
      });
    } else {
      permitGroup.markAllAsTouched();
      console.log('Form is invalid', this.form);
    }
  }

  onCostBreakDownSubmit() {
    const costBreakDownGroup = this.form.get('costBreakDownData') as FormArray;
    // console.log("Form values: ", this.form.value);
    if (costBreakDownGroup.valid) {
      const formData = this.form.value;

      const dataToSubmit = new FormData();

      console.log('costBreakDown Section Data File 1 : ', formData.costBreakDownData);

      dataToSubmit.append('costBreakDown_section', formData.costBreakDownData);

      // Submit the form using your project service
      this.projectService.addScope(dataToSubmit).subscribe({
        next: (response: any) => {
          console.log('Project scope added:', response);
          this.toast.success('Scope added successfully', 'SUCCESS', 4000);
          this.form.reset();
        },
        error: (error: any) => {
          console.error('Upload failed:', error);
          this.toast.danger('Failed to add scope', 'ERROR', 4000);
        },
      });
    } else {
      costBreakDownGroup.markAllAsTouched();
      console.log('Form is invalid', this.form);
    }
  }

}
